import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { OKTA_AUTH } from '@okta/okta-angular';
import { AuditCorrelationService } from '../../services/audit-correlation/audit-correlation.service';
import { ConfigService } from '../../services/config/config.service';
import { SessionContextService } from '../../services/session-context/session-context.service';

/**
 * Read the admin-API base URL without letting a config failure escape the interceptor.
 *
 * `ConfigService.config` deliberately **throws** `ConfigNotLoadedError` when the runtime config
 * hasn't loaded — and `ConfigService.initialize()` fetches `/config.json` through `HttpClient`,
 * so that request passes through this very interceptor. A bare read would therefore turn a
 * recoverable sync-preload miss into an unrecoverable bootstrap failure: the request that would
 * have loaded the config is the one that throws. (Optional chaining does not help — the getter
 * throws before it can yield a value.)
 *
 * Absent config just means "can't classify this request", so the audit headers are skipped. The
 * CSRF marker and bearer token are unaffected; nothing about the auth contract is weakened.
 */
function tryGetAdminApiUrl(configService: ConfigService): string | null {
  try {
    return configService.config.adminDashboardApiUrl ?? null;
  } catch {
    return null;
  }
}

/**
 * Attaches the standard CSRF marker and, when available, the Okta bearer token
 * to every outgoing HttpClient request. `getAccessToken()` reads synchronously
 * from Okta's in-memory token manager, so no async plumbing is needed here.
 *
 * The audit correlation/session headers are added only to admin-API requests
 * (matched against `config.adminDashboardApiUrl`) so we don't send custom
 * headers to Okta or other origins, which would trigger a CORS preflight.
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const token = inject(OKTA_AUTH).getAccessToken();

  const apiUrl = tryGetAdminApiUrl(inject(ConfigService));
  const isAdminApi = !!apiUrl && req.url.startsWith(apiUrl);

  const setHeaders: Record<string, string> = { 'X-CSRF': '1' };
  if (token) {
    setHeaders['Authorization'] = `Bearer ${token}`;
  }
  if (isAdminApi) {
    setHeaders['X-Correlation-Id'] = inject(AuditCorrelationService).correlationId;
    setHeaders['X-Session-Id'] = inject(SessionContextService).sessionId;
  }

  return next(req.clone({ setHeaders }));
};
